import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, Briefcase } from "lucide-react";

export default function Career() {
  const [enr, setEnr] = useState([]);
  const [certs, setCerts] = useState([]);
  const [apps, setApps] = useState([]);
  useEffect(() => {
    api.get("/me/enrollments").then((r) => setEnr(r.data)).catch(() => {});
    api.get("/me/certificates").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/me/applications").then((r) => setApps(r.data)).catch(() => {});
  }, []);

  const lessonsDone = enr.some((e) => e.lessons_completed?.length > 0);
  const examPassed = enr.some((e) => e.exam_passed);
  const practical = enr.some((e) => e.video_status === "approved");
  const hasCert = certs.length > 0;
  const applied = apps.length > 0;
  const interviewing = apps.some((a) => ["interview", "offered", "hired"].includes(a.status));

  const steps = [
    ["Completed course lessons", lessonsDone],
    ["Passed final assessment", examPassed],
    ["Completed practical training", practical],
    ["Certification issued", hasCert],
    ["Applied to FSCD opportunities", applied],
    ["Interview preparation", interviewing],
    ["Employer match", apps.some((a) => a.status === "hired")],
  ];
  const score = Math.round((steps.filter(([, d]) => d).length / steps.length) * 100);

  return (
    <div className="space-y-5 max-w-2xl">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Career Pathway</h1>
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white flex items-center gap-6">
        <div className="relative w-24 h-24 shrink-0">
          <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#334155" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f59e0b" strokeWidth="3" strokeDasharray={`${score}, 100`} strokeLinecap="round" />
          </svg>
          <span data-testid="readiness-score" className="absolute inset-0 flex items-center justify-center font-heading text-xl font-extrabold">{score}%</span>
        </div>
        <div>
          <p className="font-heading font-bold text-lg">Job Readiness Score</p>
          <p className="text-sm text-slate-300">Complete courses, certifications and applications to raise your score and unlock employer matches.</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2" data-testid="readiness-checklist">
        {steps.map(([label, done]) => (
          <div key={label} className="flex items-center gap-3 py-1.5">
            {done ? <CheckCircle2 className="text-emerald-600" size={20} /> : <Circle className="text-slate-300" size={20} />}
            <span className={done ? "text-slate-500 line-through decoration-slate-300" : "text-slate-800 font-medium"}>{label}</span>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Briefcase size={20} /></span>
        <div className="flex-1">
          <p className="font-semibold text-slate-900">Employment opportunities</p>
          <p className="text-sm text-slate-500">Certified graduates are visible to FSCD partner employers.</p>
        </div>
        <Link to="/applications" data-testid="career-applications-link" className="h-11 px-5 rounded-xl bg-teal-700 text-white font-semibold text-sm flex items-center">My Applications</Link>
      </div>
    </div>
  );
}
